import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { onboardingApi, usersApi } from '../api/client';
import { T } from '../components/ui';

interface Props {
  onComplete?: () => void;
}

const STEPS = [
  { n: 1, title: 'Company Profile',  icon: '🏢', desc: 'Tell us about your organisation' },
  { n: 2, title: 'Add a Retailer',   icon: '🛒', desc: 'Where are your products sold?' },
  { n: 3, title: 'Add a Product',    icon: '📦', desc: 'Import your first product' },
  { n: 4, title: 'Invite Your Team', icon: '👥', desc: 'Bring your colleagues on board' },
  { n: 5, title: 'All Set!',         icon: '🎉', desc: 'Your account is ready' },
];

const ROLES = ['ops_manager', 'analyst', 'auditor', 'billing_admin', 'read_only'];
const INDUSTRIES = ['Food & Beverage', 'Retail', 'Agriculture', 'Seafood', 'Pharma', 'Apparel', 'Other'];

export default function OnboardingPage({ onComplete }: Props) {
  const { user } = useAuth();
  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Step-specific state
  const [industry, setIndustry] = useState('');
  const [country, setCountry]   = useState('');
  const [retailerName, setRetailerName] = useState('');
  const [productName, setProductName]   = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [inviteEmail, setInviteEmail]     = useState('');
  const [inviteName, setInviteName]       = useState('');
  const [inviteRole, setInviteRole]       = useState('analyst');

  useEffect(() => {
    onboardingApi.status().then(s => {
      if (s.completed) { onComplete?.(); return; }
      setStep(Math.max(1, s.current_step + 1));
    }).catch(() => {});
  }, []);

  const advance = async (n: number, body?: Record<string, unknown>) => {
    setLoading(true);
    setError('');
    try {
      await onboardingApi.completeStep(n, body);
      if (n === 5) { onComplete?.(); return; }
      setStep(n + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setLoading(true);
    setError('');
    try {
      const result = await usersApi.invite({ email: inviteEmail, display_name: inviteName || inviteEmail, role: inviteRole });
      setInviteUrl(result.invite_url);
      setInviteEmail('');
      setInviteName('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Invite failed');
    } finally {
      setLoading(false);
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px',
    background: T.bg, border: `1px solid ${T.border}`,
    borderRadius: 8, color: T.text, fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, color: T.sub, marginBottom: 6, fontWeight: 500,
  };

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 540 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text }}>
            Trace<span style={{ color: T.copper }}>Full</span>
          </div>
          <div style={{ fontSize: 13, color: T.sub, marginTop: 4 }}>
            Welcome, {user?.display_name} — let's get {user?.tenant_name} set up
          </div>
        </div>

        {/* Step progress */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 32, justifyContent: 'center' }}>
          {STEPS.map(s => (
            <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: s.n < step ? T.sage : s.n === step ? T.copper : T.border,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                color: s.n <= step ? '#fff' : T.dim,
                transition: 'background 0.3s',
              }}>
                {s.n < step ? '✓' : s.n}
              </div>
              {s.n < 5 && <div style={{ width: 24, height: 1, background: s.n < step ? T.sage : T.border }} />}
            </div>
          ))}
        </div>

        {/* Card */}
        <div style={{
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: '32px 36px',
        }}>
          {STEPS.filter(s => s.n === step).map(s => (
            <div key={s.n}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: T.text }}>{s.title}</h2>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: T.sub }}>{s.desc}</p>
            </div>
          ))}

          {error && (
            <div style={{
              background: `${T.crimson}15`, border: `1px solid ${T.crimson}40`,
              borderRadius: 8, padding: '10px 14px', marginBottom: 20,
              fontSize: 13, color: T.crimson,
            }}>
              {error}
            </div>
          )}

          {/* Step 1 — Company Profile */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Industry</label>
                <select
                  value={industry}
                  onChange={e => setIndustry(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Country</label>
                <input
                  style={inputStyle}
                  placeholder="United States"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2 — Add Retailer */}
          {step === 2 && (
            <div>
              <label style={labelStyle}>Retailer name</label>
              <input
                style={inputStyle}
                placeholder="e.g. Whole Foods, Costco, Amazon Fresh"
                value={retailerName}
                onChange={e => setRetailerName(e.target.value)}
              />
            </div>
          )}

          {/* Step 3 — Add Product */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Product name</label>
                <input
                  style={inputStyle}
                  placeholder="e.g. Organic Olive Oil 500ml"
                  value={productName}
                  onChange={e => setProductName(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <input
                  style={inputStyle}
                  placeholder="e.g. Oils, Dairy, Seafood"
                  value={productCategory}
                  onChange={e => setProductCategory(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 4 — Invite team */}
          {step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Email</label>
                  <input
                    style={inputStyle}
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <label style={labelStyle}>Role</label>
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <button
                onClick={sendInvite}
                disabled={!inviteEmail || loading}
                style={{
                  padding: '10px 20px',
                  background: T.copper, color: '#fff',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                Send invite
              </button>

              {inviteUrl && (
                <div style={{
                  background: `${T.sage}15`, border: `1px solid ${T.sage}40`,
                  borderRadius: 8, padding: '10px 14px',
                }}>
                  <div style={{ fontSize: 12, color: T.sage, marginBottom: 6 }}>
                    ✓ Invite link generated — share it with your colleague
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{
                      flex: 1, fontSize: 11, color: T.sub,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {inviteUrl}
                    </code>
                    <button
                      onClick={copyInvite}
                      style={{
                        padding: '4px 10px', background: T.border,
                        border: 'none', borderRadius: 6,
                        fontSize: 11, cursor: 'pointer', color: T.text,
                        flexShrink: 0,
                      }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5 — Done */}
          {step === 5 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <p style={{ fontSize: 15, color: T.text, margin: '0 0 8px' }}>
                Your TraceFull account is ready.
              </p>
              <p style={{ fontSize: 13, color: T.sub, margin: 0 }}>
                Start by importing products, connecting suppliers, or exploring the AI agents.
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'flex-end' }}>
            {step > 1 && step < 5 && (
              <button
                onClick={() => advance(step, { skip: true })}
                style={{
                  padding: '10px 20px',
                  background: 'transparent', color: T.sub,
                  border: `1px solid ${T.border}`, borderRadius: 8,
                  fontSize: 14, cursor: 'pointer',
                }}
              >
                Skip
              </button>
            )}
            <button
              onClick={() => {
                if (step === 1) advance(1, { industry, country });
                else if (step === 2) advance(2, { name: retailerName, skip: !retailerName });
                else if (step === 3) advance(3, { name: productName, category: productCategory, skip: !productName });
                else if (step === 4) advance(4, {});
                else advance(5, {});
              }}
              disabled={loading}
              style={{
                padding: '10px 28px',
                background: loading ? T.border : T.copper,
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Saving…' : step === 5 ? 'Go to dashboard →' : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
